/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef } from 'react';
import { useLocationTreeContext } from 'components/LocationTree/context/LocationTreeContext';
import useExpandTransition from '../../hooks/useExpandTransition/useExpandTransition';
import type { Location, LocationProps } from '../../types';
import { isLeaf, isBranch } from '../../utils/validations';
import normalizeLocationId from '../../utils/normalizeLocationId';
import style from './LocationTreeNode.module.css';

const LocationTreeNode: React.FC<LocationProps> = ({
  node,
  branch: Branch,
  leaf: Leaf,
  children,
  onToggleOpen,
  depth,
}) => {
  const isBranchNode = isBranch(node);
  const isLeafNode = isLeaf(node);
  const isOpen = isBranchNode && (node.isOpen ?? false);
  const treeItemRef = useRef<HTMLLIElement | null>(null);
  const { selectedLocations, focusedNodeId, setFocusedNodeId } =
    useLocationTreeContext();
  const nodeId = normalizeLocationId(node.id);
  const isFocused = focusedNodeId === nodeId;
  const isSelected = selectedLocations.some(
    (loc: Location) => normalizeLocationId(loc.id) === nodeId
  );

  const { shouldRender, transitionState } = useExpandTransition({
    isOpen,
    transitionDuration: 300,
    disableAnimations: true,
  });

  useEffect(() => {
    if (isFocused && document.activeElement !== treeItemRef.current) {
      treeItemRef.current?.focus();
    }
  }, [isFocused]);

  const renderBranchContent =
    shouldRender && Array.isArray(children) && children.length > 0;

  return (
    <li
      ref={treeItemRef}
      id={nodeId}
      tabIndex={isFocused ? 0 : -1}
      role="treeitem"
      aria-expanded={isBranchNode ? isOpen : undefined}
      aria-level={depth}
      aria-selected={isSelected}
      className={style.locationNode}
      onFocus={(e) => {
        if (e.target === e.currentTarget) {
          setFocusedNodeId(nodeId);
        }
      }}
    >
      {isBranchNode && (
        <Branch
          key={node.id}
          branch={node}
          depth={depth}
          onToggleOpen={() => onToggleOpen(node)}
        />
      )}

      {isLeafNode && <Leaf key={nodeId} {...node} depth={depth} />}

      {isBranchNode && renderBranchContent && (
        <ul
          role="group"
          aria-label={node.name}
          className={`${style.locationNodeChildren} ${transitionState}`}
        >
          {children}
        </ul>
      )}
    </li>
  );
};

export default LocationTreeNode;
